import React from "react";
import { AlertTriangle, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";

/**
 * App-wide error boundary so a runtime crash never shows the raw red
 * "Invalid time value" / "Cannot read properties" overlay to a customer.
 *
 * Sits at the very top of the React tree (in `index.js`) and renders a
 * friendly fallback card with a "Refresh" CTA. We log the error to the
 * console so devs can still reproduce in DevTools.
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error("Postmoro ErrorBoundary caught:", error, info?.componentStack);
  }

  handleReload = () => {
    try {
      window.location.reload();
    } catch (_e) {
      this.setState({ hasError: false, error: null });
    }
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    const msg = this.state.error?.message || "Unexpected error";
    const isInvalidTime = /invalid time value/i.test(msg);

    return (
      <div
        className="min-h-screen w-full flex items-center justify-center bg-[color:var(--bg-0,#0b0e12)] text-[color:var(--text-1,#e6ebf2)] p-6"
        data-testid="error-boundary-fallback"
      >
        <div className="max-w-md w-full rounded-xl border border-[color:var(--border-subtle,#262b33)] bg-[color:var(--surface-1,#13171d)] p-6 shadow-xl">
          <div className="flex items-start gap-3">
            <div className="h-10 w-10 rounded-full bg-[color:rgba(255,92,108,0.12)] text-[color:var(--danger,#ff5c6c)] flex items-center justify-center shrink-0">
              <AlertTriangle className="h-5 w-5" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-display text-lg font-semibold">Something went wrong</div>
              <p className="text-sm text-[color:var(--text-2,#9aa3ad)] mt-1.5 leading-relaxed">
                {isInvalidTime
                  ? "We couldn't read the date / time on that action. Please refresh and pick a valid future date."
                  : "Postmoro hit an unexpected error. Refresh the page to keep going — your work is saved on every action."}
              </p>
              <pre className="mt-3 max-h-40 overflow-auto rounded-md border border-[color:var(--border-subtle,#262b33)] bg-[color:var(--surface-2,#1a1f26)] p-3 text-[11px] text-[color:var(--text-3,#6b7280)] whitespace-pre-wrap break-words" data-testid="error-boundary-message">
                {msg}
              </pre>
              <div className="mt-4 flex gap-2">
                <Button
                  onClick={this.handleReload}
                  className="bg-[color:var(--brand-primary,#1ba89f)] hover:bg-[color:var(--brand-primary-hover,#16968e)] text-[color:var(--bg-0,#0b0e12)] font-semibold"
                  data-testid="error-boundary-reload"
                >
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Refresh
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}
